import React, { useState, useEffect } from 'react'
import {
    FileText, Image, CheckCircle, XCircle,
    Download, Loader2, AlertCircle,
    FileAudio, Eye
} from 'lucide-react'
import Cookies from 'js-cookie'

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api'

export function getAuthHeaders(): HeadersInit {
    const token = Cookies.get('token')
    return token ? { 'Authorization': `Bearer ${token}` } : {}
}

// ─── Document-slot definitions ───────────────────────────────────────────────
interface DocSlot {
    key: string
    label: string
    category: 'PDF' | 'Audio' | 'ID' | 'Selfie'
    type: 'Initial' | 'Manual'
    accept: string
    icon: 'pdf' | 'audio' | 'image'
}

const DOC_SLOTS: DocSlot[] = [
    // PDFs
    { key: 'initialCmsConsentPdf', label: 'CMS Written Consent', category: 'PDF', type: 'Initial', accept: '.pdf', icon: 'pdf' },
    { key: 'initialCarrierConsentPdf', label: 'Carrier Written Consent', category: 'PDF', type: 'Initial', accept: '.pdf', icon: 'pdf' },
    { key: 'initialRenewalConsentPdf', label: 'Next-Year Renewal Consent', category: 'PDF', type: 'Initial', accept: '.pdf', icon: 'pdf' },
    // Audio
    { key: 'initialCmsAudio', label: 'CMS Voice Consent', category: 'Audio', type: 'Initial', accept: '.mp3,.wav,audio/*', icon: 'audio' },
    { key: 'initialCarrierAudio', label: 'Carrier Voice Consent', category: 'Audio', type: 'Initial', accept: '.mp3,.wav,audio/*', icon: 'audio' },
    { key: 'manualCmsAudio', label: 'CMS Voice Consent (Manual Upload)', category: 'Audio', type: 'Manual', accept: '.mp3,.wav,audio/*', icon: 'audio' },
    { key: 'manualCarrierAudio', label: 'Carrier Voice Consent (Manual Upload)', category: 'Audio', type: 'Manual', accept: '.mp3,.wav,audio/*', icon: 'audio' },
    // ID Uploads
    { key: 'initialIdUpload', label: 'Client ID Photo', category: 'ID', type: 'Initial', accept: 'image/*,.pdf', icon: 'image' },
    { key: 'manualIdUpload', label: 'Client ID Photo (Manual Upload)', category: 'ID', type: 'Manual', accept: 'image/*,.pdf', icon: 'image' },
    // Selfies
    { key: 'initialSelfie', label: 'Client Selfie', category: 'Selfie', type: 'Initial', accept: 'image/*', icon: 'image' },
    { key: 'manualSelfie', label: 'Client Selfie (Manual Upload)', category: 'Selfie', type: 'Manual', accept: 'image/*', icon: 'image' },
]

const CATEGORY_COLORS: Record<string, string> = {
    PDF: 'bg-red-100 text-red-700 border-red-200',
    Audio: 'bg-purple-100 text-purple-700 border-purple-200',
    ID: 'bg-blue-100 text-blue-700 border-blue-200',
    Selfie: 'bg-green-100 text-green-700 border-green-200'
}

function DocSlotIcon({ icon }: { icon: string }) {
    if (icon === 'pdf') return <FileText className="w-4 h-4" />
    if (icon === 'audio') return <FileAudio className="w-4 h-4" />
    return <Image className="w-4 h-4" />
}

// ─── ComplianceDocsPanel — expanded view for one session ────────────────────
export default function ComplianceDocsPanel({ sessionId }: { sessionId: string }) {
    const [detail, setDetail] = useState<any>(null)
    const [loading, setLoading] = useState(true)
    const [uploading, setUploading] = useState<string | null>(null)
    const [notification, setNotification] = useState<{ type: 'success' | 'error', message: string } | null>(null)

    useEffect(() => {
        fetch(`${API_BASE_URL}/application-data-logs/${sessionId}`, { headers: getAuthHeaders() })
            .then(r => r.json())
            .then(data => { if (data.success) setDetail(data.data) })
            .catch(console.error)
            .finally(() => setLoading(false))
    }, [sessionId])

    const handleFileUpload = async (slotKey: string, file: File) => {
        setUploading(slotKey)
        try {
            const base64 = await new Promise<string>((res, rej) => {
                const reader = new FileReader()
                reader.readAsDataURL(file)
                reader.onload = () => res(reader.result as string)
                reader.onerror = rej
            })

            const resp = await fetch(`${API_BASE_URL}/application-data-logs/${sessionId}/upload`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                body: JSON.stringify({ documentType: slotKey, fileBase64: base64, fileName: file.name })
            })
            const result = await resp.json()
            if (result.success) {
                setNotification({ type: 'success', message: `${slotKey} uploaded successfully!` })
                // Refresh detail
                const updated = await fetch(`${API_BASE_URL}/application-data-logs/${sessionId}`, { headers: getAuthHeaders() })
                const updatedData = await updated.json()
                if (updatedData.success) setDetail(updatedData.data)
            } else {
                setNotification({ type: 'error', message: result.error || 'Upload failed' })
            }
        } catch (e: any) {
            console.error('Upload failed:', e)
            setNotification({ type: 'error', message: e.message || 'Upload failed' })
        } finally {
            setUploading(null)
            setTimeout(() => setNotification(null), 3000)
        }
    }

    if (loading) return (
        <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-blue-500 mr-2" />
            <span className="text-gray-600">Loading compliance documents...</span>
        </div>
    )

    if (!detail) return (
        <div className="text-center py-8 text-gray-500">Failed to load document details.</div>
    )

    const docs = detail.documents || {}

    // Group by category
    const groups = ['PDF', 'Audio', 'ID', 'Selfie'] as const
    const slotsByCategory: Record<string, DocSlot[]> = {}
    groups.forEach(g => { slotsByCategory[g] = DOC_SLOTS.filter(s => s.category === g) })

    return (
        <div className="bg-gray-50 border-t border-gray-200 p-6">
            {notification && (
                <div className={`mb-4 px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium animate-in fade-in slide-in-from-top-2 ${notification.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
                    }`}>
                    {notification.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                    {notification.message}
                </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {groups.map(group => (
                    <div key={group} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                        <div className={`px-4 py-3 border-b font-semibold text-sm ${CATEGORY_COLORS[group]}`}>
                            {group === 'PDF' && '📄 Consent PDF Forms'}
                            {group === 'Audio' && '🎙️ Voice Consent Audio'}
                            {group === 'ID' && '🪪 ID Documents'}
                            {group === 'Selfie' && '📸 Client Selfie Photos'}
                        </div>
                        <div className="divide-y divide-gray-100">
                            {slotsByCategory[group].map(slot => {
                                const docData = docs[slot.key]
                                const hasFile = !!docData?.url
                                const isUploading = uploading === slot.key

                                return (
                                    <div key={slot.key} className="px-4 py-3 flex items-center justify-between gap-3">
                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                            <span className={`p-1.5 rounded ${hasFile ? 'text-green-600 bg-green-50' : 'text-gray-400 bg-gray-100'}`}>
                                                <DocSlotIcon icon={slot.icon} />
                                            </span>
                                            <div className="min-w-0">
                                                <div className="text-sm font-medium text-gray-800 truncate">{slot.label}</div>
                                                <div className="flex items-center gap-2 mt-0.5">
                                                    <span className={`text-xs px-1.5 py-0.5 rounded border ${slot.type === 'Initial' ? 'bg-blue-50 text-blue-600 border-blue-200' : 'bg-orange-50 text-orange-600 border-orange-200'}`}>
                                                        {slot.type}
                                                    </span>
                                                    {docData?.uploadedAt && (
                                                        <span className="text-xs text-gray-400">
                                                            {new Date(docData.uploadedAt).toLocaleDateString()}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            {hasFile ? (
                                                <>
                                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                                    {slot.icon === 'audio' ? (
                                                        <audio controls src={docData.url} className="h-7 w-32" />
                                                    ) : (
                                                        <a
                                                            href={docData.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                                                        >
                                                            <Eye className="w-3.5 h-3.5" /> View
                                                        </a>
                                                    )}
                                                    <a href={docData.url} download className="text-gray-500 hover:text-gray-700">
                                                        <Download className="w-3.5 h-3.5" />
                                                    </a>
                                                </>
                                            ) : (
                                                <div className="flex items-center gap-1.5">
                                                    <XCircle className="w-4 h-4 text-red-400" />
                                                    <label className={`cursor-pointer text-xs px-2 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700 transition-colors flex items-center gap-1 ${isUploading ? 'opacity-60 cursor-wait' : ''}`}>
                                                        {isUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3 rotate-180" />}
                                                        Upload
                                                        <input
                                                            type="file"
                                                            className="sr-only"
                                                            accept={slot.accept}
                                                            disabled={isUploading}
                                                            onChange={e => {
                                                                const f = e.target.files?.[0]
                                                                if (f) handleFileUpload(slot.key, f)
                                                            }}
                                                        />
                                                    </label>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}
